import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Button } from '../../shared/ui/button';
import { Hint } from '../../shared/ui/hint';
import { Modal } from '../../shared/ui/modal';
import { Note } from '../../shared/ui/note';
import { OptionCard } from '../../shared/ui/option-card';
import { Switch } from '../../shared/ui/switch';
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from '../../shared/ui/menu';
import { ChevronDownIcon } from '../../shared/ui/icons';
import { clockTime } from '../../shared/lib/format';
import {
  acknowledgeIssue,
  ignoreIssue,
  muteIssue,
  resolveIssue,
  setIssueAutoResolve,
  unmuteIssue,
  type TIssueDetail,
  type TMuteSpan,
} from '../../entities/issue';

const MUTE_SPANS: readonly { span: TMuteSpan; label: string }[] = [
  { span: '1h', label: 'for 1 hour' },
  { span: '4h', label: 'for 4 hours' },
  { span: '24h', label: 'for 24 hours' },
  { span: 'until_resolved', label: 'until it is resolved' },
];

interface IIssueActionsProps {
  issue: TIssueDetail;
  /** Off where the screen already has its own close button ("Mark handled"). */
  closeInMenu?: boolean;
  timeZone?: string;
}

export const IssueActions = ({ issue, closeInMenu = true, timeZone }: IIssueActionsProps) => {
  const client = useQueryClient();
  // A flow issue's subject is its step; the rule is scoped to it.
  const where = issue.flowId ? 'at this step' : 'from this source';
  const [ignoring, setIgnoring] = useState(false);
  const [createRule, setCreateRule] = useState(true);
  const [wholeClass, setWholeClass] = useState(false);

  const refresh = () => {
    void client.invalidateQueries({ queryKey: ['issue', issue.id] });
    void client.invalidateQueries({ queryKey: ['issues'] });
  };

  const acknowledge = useMutation({
    mutationFn: () => acknowledgeIssue(issue.id),
    onSuccess: refresh,
  });
  const resolve = useMutation({ mutationFn: () => resolveIssue(issue.id), onSuccess: refresh });
  const autoResolve = useMutation({
    mutationFn: (next: boolean) => setIssueAutoResolve(issue.id, next),
    onSuccess: refresh,
  });
  // Scoped like an Ignore rule, but it ends by itself: no alert, out of "Needs you", still open.
  const mute = useMutation({
    mutationFn: (span: TMuteSpan | null) =>
      span === null ? unmuteIssue(issue.id) : muteIssue(issue.id, span),
    onSuccess: refresh,
  });
  const ignore = useMutation({
    mutationFn: () => ignoreIssue(issue.id, { createRule, wholeClass }),
    onSuccess: () => {
      setIgnoring(false);
      refresh();
    },
  });

  const closed = issue.state === 'resolved' || issue.state === 'ignored';
  const failed = acknowledge.isError || resolve.isError || autoResolve.isError || mute.isError;
  const muted = issue.mutedUntilResolved
    ? 'Muted until resolved'
    : issue.mutedUntil
      ? `Muted until ${clockTime(issue.mutedUntil, timeZone)}`
      : null;

  return (
    <>
      {failed && <span className="self-center text-meta text-danger">That did not go through</span>}
      {muted && !closed && <span className="self-center text-meta text-muted">{muted}</span>}

      {issue.state === 'open' && (
        <Button
          variant="ghost"
          size="sm"
          waiting={acknowledge.isPending}
          waitingLabel="Acknowledging"
          onClick={() => acknowledge.mutate()}
        >
          Acknowledge
        </Button>
      )}

      <Menu>
        <MenuTrigger asChild>
          <Button variant="ghost" size="sm">
            More
            <ChevronDownIcon size={12} />
          </Button>
        </MenuTrigger>
        <MenuContent align="end">
          {closeInMenu && (
            <MenuItem disabled={closed} onSelect={() => resolve.mutate()}>
              Close it now
            </MenuItem>
          )}
          <MenuItem disabled={issue.state === 'ignored'} onSelect={() => setIgnoring(true)}>
            Ignore this…
          </MenuItem>
          <MenuSeparator />
          {MUTE_SPANS.map(({ span, label }) => (
            <MenuItem key={span} disabled={closed} onSelect={() => mute.mutate(span)}>
              Mute {label}
            </MenuItem>
          ))}
          {muted && !closed && <MenuItem onSelect={() => mute.mutate(null)}>Unmute now</MenuItem>}
          <MenuSeparator />
          <MenuItem
            // Without preventDefault the row select closes the menu before the switch state it reports changes.
            onSelect={(event) => event.preventDefault()}
          >
            <span>Close itself when it goes quiet</span>
            <Switch
              size="sm"
              className="ml-auto"
              checked={issue.autoResolve}
              aria-label="Close itself when it goes quiet"
              onCheckedChange={(next) => autoResolve.mutate(next)}
            />
          </MenuItem>
        </MenuContent>
      </Menu>

      <Modal
        open={ignoring}
        onOpenChange={setIgnoring}
        title="Stop being told about this"
        description="Ignoring hides the alert, not the cost. The events keep arriving and keep taking storage."
        width="md"
        footerNote={createRule ? 'A rule is added too' : 'No rule — it comes back next time'}
        submit={{
          label: 'Ignore this issue',
          onSubmit: () => ignore.mutate(),
          disabled: ignore.isPending,
          waiting: ignore.isPending,
        }}
      >
        <div className="flex flex-col gap-2">
          <OptionCard
            selected={createRule}
            title="Add a rule, so it stays ignored"
            description="Without one the Ignore lasts exactly until the next failure with the same signature."
            control={
              <Switch
                size="sm"
                checked={createRule}
                onCheckedChange={setCreateRule}
                aria-label="Add a rule, so it stays ignored"
              />
            }
          />
          <OptionCard
            selected={createRule && wholeClass}
            title={`Cover the whole error class ${where}`}
            description={`Wider than this one signature — “never open a 429 issue ${where} again”.`}
            control={
              <Switch
                size="sm"
                checked={wholeClass}
                disabled={!createRule}
                onCheckedChange={setWholeClass}
                aria-label={`Cover the whole error class ${where}`}
              />
            }
          />
        </div>
        {ignore.isError && (
          <div className="mt-3">
            <Note tone="danger">That did not go through. Nothing was ignored.</Note>
          </div>
        )}
        <Hint>
          The issue stays listed and counted under “Everything”. Only the alert goes away.
        </Hint>
      </Modal>
    </>
  );
};
